import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  createCrmLead,
  listCrmLeads,
  type CrmLead,
  type CrmLeadStatus,
} from "../../api";

function statusLabel(s: CrmLeadStatus): string {
  if (s === "new") return "Nouveau";
  if (s === "exited") return "Sorti";
  return "Actif";
}

export default function CrmLeadsList() {
  const [leads, setLeads] = useState<CrmLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<CrmLeadStatus | "">("");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [statusNew, setStatusNew] = useState<CrmLeadStatus>("active");

  const load = useCallback(async () => {
    setErr(null);
    setLoading(true);
    try {
      const data = await listCrmLeads(filter ? { status: filter } : undefined);
      setLeads(data);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Erreur de chargement");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      await createCrmLead({
        first_name: firstName.trim(),
        last_name: lastName.trim() || null,
        email: email.trim() || null,
        company: company.trim() || null,
        status: statusNew,
      });
      setFirstName("");
      setLastName("");
      setEmail("");
      setCompany("");
      setStatusNew("active");
      setShowForm(false);
      await load();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Création impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-wider text-concrete">
            Pipeline
          </p>
          <h1 className="font-sans text-3xl font-bold tracking-tight text-black">
            Leads
          </h1>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-lemonade"
        >
          {showForm ? "Fermer" : "+ Nouveau lead"}
        </button>
      </header>

      {err && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {err}
        </div>
      )}

      {showForm && (
        <form
          onSubmit={submitCreate}
          className="rounded-xl border border-stone bg-white p-6 shadow-sm space-y-4"
        >
          <h2 className="text-sm font-semibold text-charcoal">Créer un lead</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="text-concrete">Prénom *</span>
              <input
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-stone px-3 py-2"
              />
            </label>
            <label className="block text-sm">
              <span className="text-concrete">Nom</span>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-stone px-3 py-2"
              />
            </label>
            <label className="block text-sm">
              <span className="text-concrete">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-stone px-3 py-2"
              />
            </label>
            <label className="block text-sm">
              <span className="text-concrete">Entreprise</span>
              <input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="mt-1 w-full rounded-lg border border-stone px-3 py-2"
              />
            </label>
            <label className="block text-sm sm:col-span-2">
              <span className="text-concrete">Statut</span>
              <select
                value={statusNew}
                onChange={(e) => setStatusNew(e.target.value as CrmLeadStatus)}
                className="mt-1 w-full rounded-lg border border-stone px-3 py-2 sm:max-w-xs"
              >
                <option value="new">Nouveau</option>
                <option value="active">Actif</option>
                <option value="exited">Sorti</option>
              </select>
            </label>
          </div>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-charcoal px-4 py-2 text-sm font-medium text-ivory disabled:opacity-50"
          >
            {saving ? "Enregistrement…" : "Créer"}
          </button>
        </form>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <span className="font-mono text-xs uppercase text-concrete">Filtrer</span>
        <select
          value={filter}
          onChange={(e) => setFilter((e.target.value || "") as CrmLeadStatus | "")}
          className="rounded-lg border border-stone bg-white px-3 py-2 text-sm"
        >
          <option value="">Tous</option>
          <option value="new">Nouveau</option>
          <option value="active">Actif</option>
          <option value="exited">Sorti</option>
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-stone bg-white shadow-sm">
        {loading ? (
          <p className="p-6 text-concrete">Chargement…</p>
        ) : leads.length === 0 ? (
          <p className="p-6 text-concrete">
            Aucun lead pour ce filtre. Crée-en un avec le bouton ci-dessus.
          </p>
        ) : (
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="border-b border-stone bg-ivory font-mono text-[11px] uppercase tracking-wider text-concrete">
              <tr>
                <th className="px-4 py-3 font-medium">Nom</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Entreprise</th>
                <th className="px-4 py-3 font-medium">Statut</th>
                <th className="px-4 py-3 font-medium">Prochaine étape</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.lead_id} className="border-b border-stone last:border-0">
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={`/admin/crm/leads/${l.lead_id}`}
                      className="text-charcoal hover:underline"
                    >
                      {l.first_name} {l.last_name || ""}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-concrete">{l.email || "—"}</td>
                  <td className="px-4 py-3 text-concrete">{l.company || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-stone/40 px-2 py-0.5 text-xs">
                      {statusLabel(l.status)}
                    </span>
                  </td>
                  <td className="max-w-[220px] truncate px-4 py-3 text-concrete">
                    {l.next_step_label || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
